// src/components/portal/UserManager.jsx
import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
    Users,
    Plus,
    Edit,
    Trash2,
    Shield,
    Search,
    X,
    KeyRound,
    Lock,
    ArrowLeft,
} from "lucide-react";
import { getUser } from "../../auth/Auth";
import { getRequest, postRequest, deleteRequest } from "../../routes/Routes";

/* ----------------------------- Helpers ----------------------------- */

const RoleBadge = ({ roleKey }) => {
    const styles = {
        ROOT: { cls: "bg-black text-white", label: "ROOT" },
        ADMIN: { cls: "bg-purple-600 text-white", label: "Admin" },
        BASIC: { cls: "bg-gray-200 text-gray-800", label: "Basic" },
    };
    const { cls, label } = styles[roleKey] || styles.BASIC;
    return (
        <span className={`px-2 py-1 rounded-pill text-xs fw-semibold ${cls}`}>
            {label}
        </span>
    );
};

const initialForm = {
    first_name: "",
    last_name: "",
    email: "",
    role_id: 2, // default Basic
    password: "",
};

/* ----------------------------- Modal ----------------------------- */

function UserModal({
    open,
    onClose,
    onSave,
    saving,
    initial,
    canEditRole,
    isEditing,
}) {
    const [form, setForm] = useState(initial || initialForm);

    useEffect(() => {
        setForm(initial || initialForm);
    }, [initial]);

    const update = (key, value) =>
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));

    if (!open) return null;

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center"
            onClick={onClose}
        >
            <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-white rounded-4 shadow-lg w-100"
                style={{ maxWidth: 600 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                    <h5 className="mb-0 fw-bold">
                        {isEditing ? "Edit User" : "Create User"}
                    </h5>
                    <button
                        type="button"
                        className="btn btn-light rounded-circle"
                        onClick={onClose}
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-3">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">First name</label>
                            <input
                                className="form-control"
                                value={form.first_name}
                                onChange={(e) =>
                                    update("first_name", e.target.value)
                                }
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Last name</label>
                            <input
                                className="form-control"
                                value={form.last_name}
                                onChange={(e) =>
                                    update("last_name", e.target.value)
                                }
                                required
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Email</label>
                            <input
                                className="form-control"
                                type="email"
                                value={form.email}
                                onChange={(e) =>
                                    update("email", e.target.value)
                                }
                                required
                                disabled={isEditing}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Role</label>
                            <select
                                className="form-select"
                                value={form.role_id}
                                onChange={(e) =>
                                    update("role_id", Number(e.target.value))
                                }
                                disabled={!canEditRole}
                            >
                                <option value={1}>Admin</option>
                                <option value={2}>Basic</option>
                            </select>
                            {!canEditRole && (
                                <div className="form-text">
                                    Only admins can change roles.
                                </div>
                            )}
                        </div>

                        {!isEditing && (
                            <div className="col-md-6">
                                <label className="form-label">
                                    Temporary password (optional)
                                </label>
                                <input
                                    className="form-control"
                                    type="password"
                                    value={form.password}
                                    onChange={(e) =>
                                        update("password", e.target.value)
                                    }
                                    placeholder="Leave blank to auto-generate"
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2 p-3 border-top">
                    <button
                        type="button"
                        className="btn btn-light"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn gradient-secondary text-white"
                        disabled={saving}
                        onClick={() => onSave(form)}
                    >
                        {saving
                            ? "Saving..."
                            : isEditing
                            ? "Save changes"
                            : "Create user"}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

/* ----------------------------- Page ----------------------------- */

export default function UserManager() {
    const [me, setMe] = useState(undefined); // undefined = loading, null = not logged in
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("");
    const [loadingList, setLoadingList] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);

    const [banner, setBanner] = useState({ type: "", text: "" });

    /* --------- Load logged-in user first --------- */
    useEffect(() => {
        getUser()
            .then(setMe)
            .catch(() => setMe(null));
    }, []);

    const token = me?.token;
    const roleId = Number(me?.role_id ?? me?.role?.id ?? me?.roleId);
    const isAdmin = roleId === 1;

    /* --------- Fetch users ONLY after we know who we are --------- */
    const fetchUsers = useCallback(async () => {
        if (!token) return;
        setLoadingList(true);
        try {
            const { data, status } = await getRequest("/api/user", token);
            if (status === 200 && Array.isArray(data?.users || data)) {
                setRows(data.users || data);
            } else {
                setRows([]);
                setBanner({
                    type: "danger",
                    text: data?.message || "Failed to load users",
                });
            }
        } catch {
            setBanner({
                type: "danger",
                text: "Network error while loading users",
            });
        } finally {
            setLoadingList(false);
        }
    }, [token]);

    useEffect(() => {
        if (me !== undefined && token && isAdmin) {
            fetchUsers();
        }
    }, [me, token, isAdmin, fetchUsers]);

    const filtered = rows.filter((u) => {
        const q = filter.trim().toLowerCase();
        if (!q) return true;
        return (
            String(u.first_name || u.name || "")
                .toLowerCase()
                .includes(q) ||
            String(u.last_name || "")
                .toLowerCase()
                .includes(q) ||
            String(u.email || "")
                .toLowerCase()
                .includes(q) ||
            String(
                u.role_name ||
                    u.role?.name ||
                    u.role ||
                    u.user_role ||
                    u.type ||
                    ""
            )
                .toLowerCase()
                .includes(q)
        );
    });

    /* ----------------------------- Actions (admins only) ----------------------------- */

    const createUser = async (payload) => {
        if (!isAdmin) return;
        setSaving(true);
        setBanner({ type: "", text: "" });
        try {
            const { data, status } = await postRequest({
                endpoint: "/api/user",
                token,
                first_name: payload.first_name,
                last_name: payload.last_name,
                email: payload.email,
                role_id: payload.role_id,
                role: payload.role_id === 1 ? "Admin" : "Basic",
                password: payload.password || undefined,
            });
            if (status === 200 && !data?.error) {
                setBanner({
                    type: "success",
                    text: data?.message || "User created",
                });
                setShowModal(false);
                await fetchUsers();
            } else {
                setBanner({
                    type: "danger",
                    text: data?.message || "Failed to create user",
                });
            }
        } catch {
            setBanner({
                type: "danger",
                text: "Network error while creating user",
            });
        } finally {
            setSaving(false);
        }
    };

    const saveEdit = async (id, payload) => {
        if (!isAdmin) return;
        setSaving(true);
        setBanner({ type: "", text: "" });
        try {
            const { data, status } = await postRequest({
                endpoint: `/api/user/update/${id}`,
                token,
                first_name: payload.first_name,
                last_name: payload.last_name,
                role_id: payload.role_id,
                role: payload.role_id === 1 ? "Admin" : "Basic",
            });
            if (status === 200 && !data?.error) {
                setBanner({
                    type: "success",
                    text: data?.message || "User updated",
                });
                setEditing(null);
                await fetchUsers();
            } else {
                setBanner({
                    type: "danger",
                    text: data?.message || "Failed to update user",
                });
            }
        } catch {
            setBanner({
                type: "danger",
                text: "Network error while updating user",
            });
        } finally {
            setSaving(false);
        }
    };

    const deleteUserById = async (id) => {
        if (!isAdmin) return;
        if (!window.confirm("Delete this user?")) return;
        setBanner({ type: "", text: "" });
        try {
            const { data, status } = await deleteRequest(
                `/api/user/${id}`,
                token
            );
            if (status === 200 && !data?.error) {
                setBanner({
                    type: "success",
                    text: data?.message || "User deleted",
                });
                setRows((prev) =>
                    prev.filter((x) => String(x.id) !== String(id))
                );
            } else {
                setBanner({
                    type: "danger",
                    text: data?.message || "Failed to delete user",
                });
            }
        } catch {
            setBanner({
                type: "danger",
                text: "Network error while deleting user",
            });
        }
    };

    const resetPassword = async (id) => {
        if (!isAdmin) return;
        if (
            !window.confirm(
                "Send password reset / set-password link for this user?"
            )
        )
            return;
        setBanner({ type: "", text: "" });
        try {
            const { data, status } = await postRequest({
                endpoint: `/api/user/edit/${id}`,
                token,
                action: "reset_password",
            });
            if (status === 200 && !data?.error) {
                setBanner({
                    type: "success",
                    text: data?.message || "Reset email sent",
                });
            } else {
                setBanner({
                    type: "danger",
                    text: data?.message || "Failed to send reset",
                });
            }
        } catch {
            setBanner({
                type: "danger",
                text: "Network error while sending reset",
            });
        }
    };

    /* ----------------------------- Render ----------------------------- */

    // 1) Loading auth state
    if (me === undefined) {
        return (
            <div className="d-flex align-items-center justify-content-center vh-100">
                <div className="text-center">
                    <div className="spinner-border text-secondary" />
                    <div className="text-muted small mt-2">
                        Loading session...
                    </div>
                </div>
            </div>
        );
    }

    // 2) Not admin: do not show Users page
    if (!isAdmin) {
        return (
            <div className="d-flex align-items-center justify-content-center vh-100 px-3">
                <div
                    className="bg-white border rounded-4 p-4 p-md-5 text-center shadow-sm"
                    style={{ maxWidth: 520 }}
                >
                    <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-25 mb-3"
                        style={{ width: 64, height: 64 }}
                    >
                        <Lock size={28} className="text-warning" />
                    </div>
                    <h2 className="h4 fw-bold mb-2">Not authorized</h2>
                    <p className="text-muted mb-4">
                        You do not have permission to view the Users page.
                    </p>
                    <a
                        href="/portal/qrs"
                        className="btn btn-outline-secondary rounded-3"
                    >
                        <ArrowLeft size={16} className="me-2" />
                        Back to Portal
                    </a>
                </div>
            </div>
        );
    }

    // 3) Admin: full Users page
    return (
        <div className="p-4 p-md-5">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div
                        className="rounded-3 text-white d-flex align-items-center justify-content-center"
                        style={{
                            width: 48,
                            height: 48,
                            background:
                                "linear-gradient(135deg, #7e22ce, #db2777)",
                        }}
                    >
                        <Users size={24} />
                    </div>
                    <div>
                        <h1 className="fs-3 fw-bold mb-0">Manage Users</h1>
                        <div className="text-muted small">
                            Create admins and basic users, edit roles, and
                            manage access.
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    className="btn gradient-secondary text-white rounded-3 fw-semibold d-flex align-items-center gap-2 shadow"
                    onClick={() => setShowModal(true)}
                    title="Create new user"
                >
                    <Plus size={18} /> New User
                </button>
            </div>

            {banner.text && (
                <div
                    className={`alert alert-${banner.type} rounded-3 py-2 px-3`}
                >
                    {banner.text}
                </div>
            )}

            {/* Toolbar */}
            <div className="bg-white rounded-3 border p-3 mb-3">
                <div className="row g-2 align-items-center">
                    <div className="col-md-6">
                        <div className="position-relative">
                            <Search
                                className="position-absolute top-50 translate-middle-y text-muted"
                                style={{ left: 12 }}
                                size={18}
                            />
                            <input
                                className="form-control ps-5"
                                placeholder="Search by name, email or role..."
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-6 text-end">
                        <span className="text-muted small">
                            {filtered.length} users
                        </span>
                    </div>
                </div>
            </div>

            {/* Table-like list */}
            <div className="bg-white rounded-3 border overflow-auto">
                <table className="table align-middle mb-0">
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: "50%" }}>Name</th>
                            <th style={{ width: "20%" }}>Role</th>
                            <th style={{ width: "30%" }} className="text-end">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loadingList ? (
                            <tr>
                                <td colSpan={3} className="text-center py-5">
                                    <div className="spinner-border text-secondary" />
                                    <div className="text-muted small mt-2">
                                        Loading users...
                                    </div>
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="text-center py-5 text-muted">
                                    No users found
                                </td>
                            </tr>
                        ) : (
                            filtered.map((u, i) => {
                                const fullName =
                                    `${u.first_name || ""} ${u.last_name || ""}`
                                        .trim() || u.name || "";

                                const rawRoleId = Number(
                                    u.role_id ?? u.role?.id
                                );
                                const rawRoleName = String(
                                    u.role_name ??
                                        u.role?.name ??
                                        u.role ??
                                        u.user_role ??
                                        u.type ??
                                        ""
                                );
                                const upperRoleName = rawRoleName.toUpperCase();

                                let effectiveRoleId = 2;
                                let uRoleKey = "BASIC";

                                if (rawRoleId === 1 || upperRoleName === "ADMIN") {
                                    effectiveRoleId = 1;
                                    uRoleKey = "ADMIN";
                                } else if (upperRoleName === "ROOT") {
                                    // Treat ROOT as highest role in UI
                                    effectiveRoleId = 1;
                                    uRoleKey = "ROOT";
                                }
                                const isSelf =
                                    !!me &&
                                    String(u.email || "").toLowerCase() ===
                                        String(me.email || "").toLowerCase();

                                return (
                                    <motion.tr
                                        key={u.id || i}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.03 }}
                                    >
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                <div
                                                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: 40,
                                                        height: 40,
                                                    }}
                                                >
                                                    <Shield
                                                        size={18}
                                                        className="text-secondary"
                                                    />
                                                </div>
                                                <div>
                                                    <div className="fw-semibold">
                                                        {fullName ||
                                                            "(No name)"}
                                                    </div>
                                                    <div className="text-muted small">
                                                        {u.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <RoleBadge roleKey={uRoleKey} />
                                        </td>
                                        <td className="text-end">
                                            <div className="d-inline-flex align-items-center gap-2">
                                                {!isSelf && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm rounded-3 d-none d-md-inline-flex align-items-center gap-2"
                                                        onClick={() =>
                                                            resetPassword(u.id)
                                                        }
                                                        title="Send password reset"
                                                    >
                                                        <KeyRound size={16} />{" "}
                                                        Reset
                                                    </button>
                                                )}

                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm rounded-3 d-inline-flex align-items-center gap-2"
                                                        onClick={() =>
                                                            setEditing({
                                                                id: u.id,
                                                                first_name:
                                                                    u.first_name ||
                                                                    "",
                                                                last_name:
                                                                    u.last_name ||
                                                                    "",
                                                                email:
                                                                    u.email || "",
                                                                role_id: effectiveRoleId,
                                                            })
                                                        }
                                                        title="Edit user"
                                                    >
                                                    <Edit size={16} /> Edit
                                                </button>

                                                {!isSelf && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm rounded-3 d-inline-flex align-items-center gap-2"
                                                        onClick={() =>
                                                            deleteUserById(
                                                                u.id
                                                            )
                                                        }
                                                        title="Delete user"
                                                    >
                                                        <Trash2 size={16} />{" "}
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </motion.tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create */}
            <UserModal
                open={showModal}
                onClose={() => setShowModal(false)}
                onSave={createUser}
                saving={saving}
                canEditRole={true}
                isEditing={false}
            />

            {/* Edit */}
            <UserModal
                open={!!editing}
                onClose={() => setEditing(null)}
                onSave={(form) => editing && saveEdit(editing.id, form)}
                saving={saving}
                initial={editing || undefined}
                canEditRole={true}
                isEditing={true}
            />
        </div>
    );
}
